import { Badge, Select } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import {
  useGetHotel,
  useGetPackageByHotelId,
} from 'app/hooks/data/hotels/useHotel';
import { useGetHotelByParentId } from 'app/hooks/data/migrationTool/useGetHotelByParentId';
import { useGetPackageCover } from 'app/hooks/data/packages/usePackages';
import { ChannelManager } from 'app/typings/channelManagers';
import { RateMode } from 'app/typings/rateModes';

import { Arrow } from '../../_components/Icons/Arrow';
import { Share } from '../../_components/Icons/Share';

import './Hotel.scss';

const { Option } = Select;

type HotelProps = {
  handleSelectChild: (childId: number) => void;
  selectChildId?: number;
  hotelId: number;
  setIsFullyLoaded: (isLoaded: boolean) => void;
};

export const Hotel = ({
  handleSelectChild,
  selectChildId,
  hotelId,
  setIsFullyLoaded,
}: HotelProps) => {
  const { data: hotelWithDetailsList } = useGetHotelByParentId(hotelId);

  const { data: hotel, isLoading: hotelLoading } = useGetHotel(hotelId);
  const { data: packagesList, isLoading: packagesLoading } =
    useGetPackageByHotelId(hotelId);
  const mainParentHotelPackage = packagesList?.packages[0];

  const { data: packageCover, isLoading: coverLoading } = useGetPackageCover(
    mainParentHotelPackage?.id ?? null
  );

  const [localChildHotelId, setLocalChildHotelId] = useState(selectChildId);

  useEffect(() => {
    if (!hotelLoading && !packagesLoading && !coverLoading) {
      setIsFullyLoaded(true);
    }
  }, [coverLoading, hotelLoading, packagesLoading, setIsFullyLoaded]);

  const childHotels = hotelWithDetailsList?.filter(
    (h) => h.hotel.id !== hotelId
  );

  const [error, setError] = useState<string | undefined>(undefined);

  const validateChildHotel = useCallback(
    (value: string): string | undefined => {
      const parsedValue = parseInt(value, 10);
      const selectedChildHotel = childHotels?.find(
        (h) => h.hotel.id === parsedValue
      );

      setLocalChildHotelId(parsedValue);

      if (
        selectedChildHotel?.hotel.pspOnboardingDone !==
          hotel?.pspOnboardingDone ||
        selectedChildHotel?.hotel.pspId !== hotel?.pspId ||
        selectedChildHotel?.hotel.issuingActivated !== hotel?.issuingActivated
      ) {
        return 'The payment configuration is not the same on these two hotels. Please set the same to continue merge';
      }

      if (hotel && hotel.rateMode === RateMode.MULTI_RATE) {
        return 'The parent hotel is on a multi-rate connectivity';
      }

      if (
        selectedChildHotel &&
        selectedChildHotel.hotel.channelManager &&
        selectedChildHotel.hotel.channelManager !== ChannelManager.NONE
      ) {
        return 'The child hotel has a connectivity';
      }

      if (selectedChildHotel?.hotel.stockScrapperEnabled) {
        return 'The child hotel has stock scrapper enabled';
      }

      if (hotel?.finance?.iban !== selectedChildHotel?.hotel.iban) {
        return 'The child hotel has a different IBAN';
      }

      handleSelectChild(parsedValue);

      return undefined;
    },
    [childHotels, hotel, handleSelectChild]
  );

  useEffect(() => {
    if (localChildHotelId) {
      const validationError = validateChildHotel(localChildHotelId.toString());

      setError(validationError);
    }
  }, [hotelWithDetailsList, hotel, localChildHotelId, validateChildHotel]);

  const handleSelectChange = (value: string) => {
    const validationError = validateChildHotel(value);

    setError(validationError);
  };

  return (
    <div className="hotel-migration-hotel-part">
      <div className="text">
        <div className="emoji">👋</div>
        <div className="merge-text">
          <div>You are about to merge an hotel with</div>
          <div className="bold">{hotel?.name}</div>
        </div>
        <div className="explanation">
          <div>
            The child hotel will be merged with the parent hotel after
            migration.
          </div>
          <div>
            This means that the hotel will be able to access both packages from
            the same Extranet.
          </div>
        </div>
      </div>
      <div className="selection">
        <div className="child-hotel">
          <div className="child-hotel-header">
            <div className="title">Child Hotel</div>
            {localChildHotelId && (
              <div
                className="actions"
                onClick={() =>
                  window.open(`/hotels/${localChildHotelId}/edit`, '_blank')
                }
              >
                <Share color="#8C8C8C" />
              </div>
            )}
          </div>
          <div className="select">
            <Select
              onChange={handleSelectChange}
              placeholder="Select a hotel"
              defaultValue={selectChildId?.toString()}
            >
              {childHotels?.map((ch) => (
                <Option key={ch.hotel.id} value={ch.hotel.id.toString()}>
                  {ch.hotel.name}
                </Option>
              ))}
            </Select>
          </div>
          <div className="error-child-hotel">{error}</div>
        </div>
        <div className="arrow">
          <Arrow />
        </div>
        <div className="parent-hotel">
          <div className="left-block">
            <div className="image-container">
              <PictureComponent
                pictureId={packageCover?.horizontalCoverPicture.pictureId ?? ''}
                alt={packageCover?.horizontalCoverPicture.alt ?? ''}
                className="image"
              />
            </div>
            <div className="texts">
              <div className="title">{hotel?.name}</div>
              <div className="aliasForHotel">
                {mainParentHotelPackage?.aliasForHotel}
              </div>
              <div className="status">
                <Badge
                  status={
                    mainParentHotelPackage?.published ? 'success' : 'error'
                  }
                />
                {mainParentHotelPackage?.published
                  ? 'Published'
                  : 'Unpublished'}
              </div>
            </div>
          </div>
          <div
            className="actions"
            onClick={() => window.open(`/hotels/${hotel?.id}/edit`, '_blank')}
          >
            <Share color="#8C8C8C" />
          </div>
        </div>
      </div>
    </div>
  );
};
